// src/components/ErrorBoundary/ErrorFallback.jsx
import React from 'react';
import { Button, Container, Row, Col } from 'react-bootstrap';

const ErrorFallback = ({ error, resetErrorBoundary, componentName }) => {
    const getErrorMessage = (error) => {
        if (!error) return 'An unexpected error occurred';
        if (typeof error === 'string') return error;
        if (error.message) {
            let message = error.message;
            message = message.replace(/^TypeError:\s*/, '');
            message = message.replace(/^ReferenceError:\s*/, '');
            message = message.replace(/^SyntaxError:\s*/, '');
            message = message.replace(/^Error:\s*/, '');
            return message;
        }
        return 'An unexpected error occurred';
    };

    const errorMessage = getErrorMessage(error);

    return (
        <Container
            fluid
            className="d-flex align-items-center justify-content-center"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                margin: 0,
                padding: '20px',
                backgroundColor: '#f8f9fa',
                zIndex: 9999,
                overflowY: 'auto'
            }}
        >
            <Row className="w-100 justify-content-center" style={{ height: '100%' }}>
                <Col xs={12} sm={11} md={11} lg={10} xl={9} xxl={8}>
                    {/* Main Card */}
                    <div
                        className="text-center d-flex flex-column justify-content-center"
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '20px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                            padding: '60px 50px',
                            border: '1px solid #e9ecef',
                            minHeight: '70vh',
                            height: 'auto',
                            maxHeight: 'calc(100vh - 40px)',
                            overflowY: 'auto'
                        }}
                    >
                        {/* Icon*/}
                        <div className="mb-4">
                            <span style={{ fontSize: '72px' }}>⚠️</span>
                        </div>

                        {/* Title*/}
                        <h2 style={{
                            color: '#e74c3c',
                            fontWeight: 700,
                            fontSize: 'clamp(28px, 4vw, 40px)',
                            marginBottom: '20px'
                        }}>
                            Something went wrong
                        </h2>

                        {/* Component Name*/}
                        {componentName && (
                            <div style={{
                                backgroundColor: '#fff8e1',
                                border: '1px solid #ffcc02',
                                borderRadius: '10px',
                                padding: '12px 28px',
                                display: 'inline-block',
                                marginBottom: '24px',
                                fontSize: '16px'
                            }}>
                                <span style={{ color: '#856404', fontSize: '15px' }}>
                                    <strong>Error in:</strong> {componentName}
                                </span>
                            </div>
                        )}

                        {/* Message*/}
                        <p style={{
                            color: '#6c757d',
                            fontSize: 'clamp(16px, 1.5vw, 20px)',
                            marginBottom: '32px',
                            maxWidth: '560px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            lineHeight: 1.6
                        }}>
                            We couldn't load this section. Please try again or refresh the page.
                        </p>

                        {/* Buttons */}
                        <div className="d-flex gap-4 justify-content-center flex-wrap">
                            <Button
                                variant="primary"
                                onClick={resetErrorBoundary}
                                className="px-5 py-3"
                                style={{
                                    borderRadius: '10px',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    minWidth: '160px'
                                }}
                            >
                                🔄 Try Again
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => window.location.reload()}
                                className="px-5 py-3"
                                style={{
                                    borderRadius: '10px',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    minWidth: '160px',
                                    borderWidth: '2px'
                                }}
                            >
                                🔄 Refresh Page
                            </Button>
                        </div>

                        {/* Error Message */}
                        {error && process.env.NODE_ENV === 'development' && (
                            <div style={{
                                marginTop: '28px',
                                textAlign: 'left',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '10px',
                                padding: '16px 20px',
                                maxWidth: '90%',
                                marginLeft: 'auto',
                                marginRight: 'auto'
                            }}>
                                <div style={{
                                    fontWeight: 600,
                                    color: '#991b1b',
                                    fontSize: '14px',
                                    marginBottom: '6px'
                                }}>
                                    Error:
                                </div>
                                <div style={{
                                    color: '#7f1d1d',
                                    fontSize: '14px',
                                    fontFamily: 'monospace',
                                    wordWrap: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(0,0,0,0.04)'
                                }}>
                                    {errorMessage}
                                </div>
                            </div>
                        )}

                        {/* Help Text */}
                        <div style={{ marginTop: '24px' }}>
                            <small style={{
                                color: '#adb5bd',
                                fontSize: '14px',
                                display: 'block'
                            }}>
                                If the problem persists, please contact our support team.
                            </small>
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default ErrorFallback;